import SplitPanel from "components/details-page/SplitPanel";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  MenuItem,
  Switch,
  Alert,
  Chip,
} from "@mui/material";
import TextField from "components/common/TextField";
import { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import { ArrowLeft, Trash2, MessagesSquare } from "lucide-react";
import ClientOutputVisibleIndicator from "components/tools/ClientOutputVisibleIndicator";
import MiniFeed from "components/forum/MiniFeed";
import { ActivityEntityType } from "protogen/common_pb";
import SimpleRichEditor, { Handle } from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import {
  useUpdateAgentPrompt,
  useFetchPromptInvocation,
  useToggleAgentPrompt,
} from "services/agents";
import {
  AgentPrompt,
  UpdateAgentPromptRequest,
  FetchPromptInvocationResponse_PromptInvocation,
} from "protogen/agents_service_pb";
import { ContentUpdates } from "protogen/notes_pb";
import DocumentViewer from "components/editor/DocumentViewer";
import DateDisplay from "components/common/DateDisplay";
import LinkRouter from "components/navigation/LinkRouter";
import { StageChip } from "components/tasks/workflowStates";
import Loading from "components/common/Loading";
import { useListAgentPrompts } from "services/agents";
import { ListAgentPromptsRequest } from "protogen/agents_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import Select from "components/common/Select";
import PlaybookTestDialog from "components/tools/PlaybookTestDialog";
import AgentToolsSelector from "components/tools/AgentToolsSelector";

type ToolsConfiguration = {
  always?: string[];
  never?: string[];
};

export default () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { ref } = useParams<{ ref: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = searchParams.get("new") === "1";
  const [showBanner, setShowBanner] = useState(isNew);
  const [playbook, setPlaybook] = useState<AgentPrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("public");
  const [instructions, setInstructions] = useState<RichContent | null>(null);
  const [invocations, setInvocations] = useState<
    FetchPromptInvocationResponse_PromptInvocation[]
  >([]);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [clientOutputVisible, setClientOutputVisible] = useState(false);
  const [toolsConfig, setToolsConfig] = useState<ToolsConfiguration>({});
  const [toolsModified, setToolsModified] = useState(false);
  const showComments = searchParams.has("comments");
  const editorRef = useRef<Handle>(null);
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const isOwner = playbook?.owner?.ref === currentUser.ref;
  const { request: listAgentPrompts } = useListAgentPrompts();
  const { request: updateAgentPrompt, loading: updateLoading } =
    useUpdateAgentPrompt();
  const { request: toggleAgentPrompt } = useToggleAgentPrompt();
  const { request: fetchInvocations, loading: loadingInvocations } =
    useFetchPromptInvocation();

  useEffect(() => {
    loadPlaybook();
  }, [ref]);

  useEffect(() => {
    if (playbook) {
      loadInvocations();
    }
  }, [playbook]);

  const loadPlaybook = async () => {
    if (!ref) return;
    setLoading(true);
    const response = await listAgentPrompts(
      new ListAgentPromptsRequest({ type: "playbook" }),
    );
    if (response) {
      const found = response.prompts.find((p) => p.ref === ref);
      if (found) {
        setPlaybook(found);
        setName(found.name);
        setScope(found.scope || "public");
        setClientOutputVisible(found.clientOutputVisible);
        if (found.configurationJson) {
          const config = JSON.parse(found.configurationJson);
          setToolsConfig(config.tools || {});
        } else {
          setToolsConfig({});
        }
        setToolsModified(false);
      }
    }
    setLoading(false);
  };

  const loadInvocations = async () => {
    if (!playbook) return;
    const response = await fetchInvocations({
      promptRef: playbook.ref,
    });
    if (response?.invocations) {
      setInvocations(response.invocations);
    }
  };

  const buildConfigurationJson = (): string => {
    const hasToolsConfig =
      (toolsConfig.always?.length || 0) + (toolsConfig.never?.length || 0) > 0;
    const config = {
      ...(hasToolsConfig ? { tools: toolsConfig } : {}),
    };
    return Object.keys(config).length > 0 ? JSON.stringify(config) : "";
  };

  const handleUpdate = async () => {
    if (!playbook || !name.trim()) return;

    const contentUpdates = instructions
      ? new ContentUpdates({
          contentType: "html",
          payload: instructions.html || "",
          textContent: instructions.text || "",
          attachments: instructions.attachments || [],
        })
      : undefined;

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: playbook.ref,
        type: "playbook",
        name: name.trim(),
        status: playbook.status,
        content: contentUpdates,
        scope: scope,
        configurationJson: buildConfigurationJson(),
        updateConfiguration: true,
      }),
    );

    if (response) {
      await loadPlaybook();
      setEditMode(false);
      setInstructions(null);
    }
  };

  const handleToolsClose = async () => {
    if (!toolsModified || !playbook) return;

    await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: playbook.ref,
        type: "playbook",
        configurationJson: buildConfigurationJson(),
        updateConfiguration: true,
      }),
    );
    setToolsModified(false);
  };

  const handleScopeChange = async (newScope: string) => {
    if (!playbook) return;
    setScope(newScope);

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: playbook.ref,
        type: "playbook",
        name: playbook.name,
        status: playbook.status,
        scope: newScope,
      }),
    );

    if (response) {
      await loadPlaybook();
    }
  };

  // const handleStatusChange = async (status: PlaybookStatus) => {
  //   if (!playbook) return;
  //   if (!(isAdmin || isOwner)) return;

  //   const response = await updateAgentPrompt(
  //     new UpdateAgentPromptRequest({
  //       ref: playbook.ref,
  //       type: "playbook",
  //       name: playbook.name,
  //       status: status,
  //     }),
  //   );

  //   if (response) {
  //     await loadPlaybook();
  //   }
  // };

  const handleToggleEnabled = async () => {
    if (!playbook) return;
    const response = await toggleAgentPrompt({
      promptRef: playbook.ref,
      isEnabled: !playbook.isEnabledForUser,
    });
    if (response) {
      await loadPlaybook();
    }
  };

  const handleToggleClientOutputVisible = async () => {
    if (!playbook) return;
    const newValue = !clientOutputVisible;
    setClientOutputVisible(newValue);
    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: playbook.ref,
        type: "playbook",
        clientOutputVisible: newValue,
        updateClientOutputVisible: true,
      }),
    );
    if (response) {
      await loadPlaybook();
    }
  };

  const handleDelete = async () => {
    if (!playbook) return;
    if (!window.confirm("Are you sure you want to delete this playbook?"))
      return;

    const response = await updateAgentPrompt(
      new UpdateAgentPromptRequest({
        ref: playbook.ref,
        type: "playbook",
        status: "disabled",
      }),
    );

    if (response) {
      navigate("/tools/agents/playbooks");
    }
  };

  const getOwnerDisplay = () => {
    if (!playbook) return "";
    const ownerName = playbook.owner?.displayName || "";
    if (
      ownerName.toLowerCase() === "faye" ||
      ownerName.toLowerCase() === "faye admin"
    ) {
      return "Faye";
    }
    return ownerName;
  };

  const getScopeLabel = () => {
    if (scope === "system-default") return "System Default";
    if (scope === "public") return "Public";
    if (scope === "private") return "Private";
    return "";
  };

  const getScopeVariant = ():
    | "teal"
    | "red"
    | "gray"
    | "green"
    | "warning"
    | "outlined" => {
    if (scope === "system-default") return "teal";
    if (scope === "public") return "green";
    if (scope === "private") return "gray";
    return "gray";
  };
  if (!playbook) {
    return (
      <SplitPanel
        leftPanel={
          <Box
            sx={{
              padding: isMobile ? "20px" : "32px 48px",
              overflow: "auto",
              height: "100%",
            }}
          >
            <Box sx={{ margin: "0 auto", maxWidth: "800px" }}>
              {loading ? (
                <Loading />
              ) : (
                <Typography variant="body">Skill not found</Typography>
              )}
            </Box>
          </Box>
        }
        rightPanel={null}
        leftPanelMaxWidth="800px"
      />
    );
  }

  const htmlContent = playbook.content?.content?.payload || "";

  const leftPanelContent = (
    <Box
      sx={{
        padding: isMobile ? "20px" : "32px 48px",
        overflow: "auto",
        height: "100%",
      }}
    >
      <Box
        sx={{
          margin: "0 auto",
          maxWidth: "800px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {/* Header */}
          <Box>
            <Button
              variant="text"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate("/tools/agents/playbooks")}
              sx={{ marginBottom: "16px", padding: "4px 8px" }}
            >
              Back to playbooks
            </Button>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box flex="1">
                {editMode ? (
                  <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    variant="standard"
                    placeholder="Playbook name"
                    margin="none"
                    InputProps={{
                      disableUnderline: true,
                      sx: (theme) => ({
                        ...theme.typography.h3,
                        input: {
                          padding: "0 0 0",
                        },
                      }),
                    }}
                    sx={{ marginBottom: "6px" }}
                  />
                ) : (
                  <Typography variant="h3" sx={{ marginBottom: "8px" }}>
                    {playbook.name}
                  </Typography>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  gap="4px"
                >
                  <Typography variant="body" color="#667085">
                    Created by {getOwnerDisplay()}
                    {" •"}
                  </Typography>
                  <DateDisplay
                    date={new Date(Number(playbook.creationSec) * 1000)}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gap="8px"
                alignItems="flex-end"
              >
                <Switch
                  disabled={updateLoading}
                  onChange={handleToggleEnabled}
                  checked={playbook.isEnabledForUser}
                  size="small"
                  sx={{
                    padding: 0,
                  }}
                />
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap="8px"
                >
                  <ClientOutputVisibleIndicator
                    visible={clientOutputVisible}
                    onClick={
                      isAdmin || isOwner
                        ? handleToggleClientOutputVisible
                        : undefined
                    }
                    disabled={updateLoading}
                  />
                  <AgentToolsSelector
                    value={toolsConfig}
                    onChange={(newConfig) => {
                      setToolsConfig(newConfig);
                      setToolsModified(true);
                    }}
                    onClose={handleToolsClose}
                    disabled={updateLoading || !(isAdmin || isOwner)}
                  />
                  {isAdmin || isOwner ? (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={scope}
                        onChange={(e) => handleScopeChange(e.target.value)}
                        size="small"
                        disabled={updateLoading}
                        sx={{ fontSize: "14px" }}
                      >
                        <MenuItem value="private">Private</MenuItem>
                        <MenuItem value="public">Public</MenuItem>
                        {isAdmin && (
                          <MenuItem value="system-default">
                            System Default
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      label={getScopeLabel()}
                      size="small"
                      variant={getScopeVariant()}
                      sx={{ height: "24px", fontSize: "12px" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider />
          {showBanner && (
            <Alert
              severity="info"
              onClose={() => {
                setShowBanner(false);
                searchParams.delete("new");
                setSearchParams(searchParams, { replace: true });
              }}
            >
              <Typography variant="body">
                <strong>Playbook created!</strong> Test your new playbook by
                creating a task and selecting it from the playbook menu.
              </Typography>
            </Alert>
          )}

          {/* Instructions */}
          {loading && !playbook ? (
            <Loading />
          ) : (
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom="12px"
              >
                <Typography variant="bodyHeavy">Instructions</Typography>
                <Box display="flex" gap="8px">
                  {editMode ? (
                    <>
                      <Button
                        variant="text"
                        onClick={() => {
                          setEditMode(false);
                          setName(playbook.name);
                          setInstructions(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUpdate}
                        disabled={!name.trim() || updateLoading}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button variant="text" onClick={() => setEditMode(true)}>
                      Edit
                    </Button>
                  )}
                </Box>
              </Box>
              {editMode ? (
                <Box>
                  <SimpleRichEditor
                    ref={editorRef}
                    setContent={setInstructions}
                    initialContent={playbook?.content?.content?.payload || ""}
                    placeholder="Enter detailed instructions for this skill..."
                    editorMinHeight="200px"
                    forceOpen={true}
                    allowAttachments={false}
                    isMarkdownTextMode={true}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #ECECEC",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "#FFF",
                  }}
                >
                  <DocumentViewer
                    key={`${playbook.updatedSec}`}
                    content={htmlContent}
                    attachments={playbook.content?.content?.attachments || []}
                    minHeight="20px"
                  />
                </Box>
              )}
              {(isOwner || isAdmin) && (
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="text"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleDelete}
                    color="error"
                    sx={{
                      "&:hover": {
                        backgroundColor: "unset",
                        border: "none",
                      },
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Recent Invocations */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="12px"
            >
              <Typography variant="bodyHeavy">Recent Invocations</Typography>
              <Button variant="text" onClick={() => setTestDialogOpen(true)}>
                Try it out
              </Button>
            </Box>
            {loadingInvocations ? (
              <Typography variant="body" color="#666">
                Loading invocations...
              </Typography>
            ) : invocations.length === 0 ? (
              <Typography variant="body" color="#666">
                No invocations found
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ boxShadow: "none", border: "1px solid #ECECEC" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Executed by
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Task Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invocations.map((invocation, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <DateDisplay
                            date={
                              new Date(Number(invocation.createdSec) * 1000)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {invocation.runner ? (
                            <LinkRouter
                              to={`/tools/advisor-administration/${encodeURIComponent(
                                invocation.runner.ref || "",
                              )}`}
                              useColorScheme
                            >
                              {invocation.runner.displayName}
                            </LinkRouter>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {invocation.taskRef ? (
                            <LinkRouter
                              to={`/tasks/${encodeURIComponent(
                                invocation.taskRef,
                              )}`}
                              useColorScheme
                            >
                              {invocation.taskTitle || invocation.taskRef}
                            </LinkRouter>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {invocation.taskStage ? (
                            <StageChip
                              stage={invocation.taskStage}
                              accountType="member"
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>

        <PlaybookTestDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          playbookRef={playbook.ref}
        />
      </Box>
    </Box>
  );

  const rightPanelContent = (
    <MiniFeed
      entityType={ActivityEntityType.PLAYBOOK_CREATED}
      entityRef={playbook.ref}
      hideHeader={true}
      onCommentsLoaded={setCommentCount}
    />
  );

  return (
    <SplitPanel
      leftPanel={leftPanelContent}
      rightPanel={rightPanelContent}
      rightPanelIcon={<MessagesSquare size={20} />}
      rightPanelTitle={`Comments (${commentCount})`}
      leftPanelMaxWidth="800px"
      defaultCollapsed={!showComments}
    />
  );
};
